import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/layout/Header';
import NavigationButton from '../../components/layout/Navigation';
import { getEvents, updateEvent, deleteEvent, completeEvent } from '../../services/eventApi';
import { getMediaUrl } from '../../utils/mediaUtils';

const API = process.env.REACT_APP_API_BASE_URL;

const getAuth = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return { headers: { Authorization: `Bearer ${user?.access}` } };
};

const DashboardEventPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const idParam = queryParams.get('id');

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState(idParam || '');
    const [filterStatus, setFilterStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [previewImage, setPreviewImage] = useState(null);
    
    // Approval Modal States
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [approvingEvent, setApprovingEvent] = useState(null);
    const [visibilityToggle, setVisibilityToggle] = useState('public');

    // Event Completion Modal States
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [completingEvent, setCompletingEvent] = useState(null);
    const [blastUseDefault, setBlastUseDefault] = useState(true);
    const [customBlastMessage, setCustomBlastMessage] = useState('');
    const [completingLoading, setCompletingLoading] = useState(false);

    const fetchEvents = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = { page };
            if (searchQuery) params.search = searchQuery;
            if (filterStatus) params.status = filterStatus;
            
            const response = await getEvents(params);
            if (response.data.results) {
                setEvents(response.data.results);
                setTotalCount(response.data.count);
                setTotalPages(Math.ceil(response.data.count / 10));
            } else {
                setEvents(response.data);
                setTotalCount(response.data.length);
                setTotalPages(1);
            }
        } catch (err) { console.error(err); }
        setLoading(false);
    }, [searchQuery, filterStatus]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || user.role !== 'admin') { navigate('/dashboard'); return; }
        fetchEvents(currentPage);
    }, [navigate, fetchEvents, currentPage]);

    const handleStatusUpdate = async (slug, newStatus) => {
        if (newStatus === 'approved') {
            const event = events.find(e => e.slug === slug);
            setApprovingEvent(event);
            setVisibilityToggle(event?.visibility || 'public');
            setShowApproveModal(true);
            return;
        }

        try {
            await updateEvent(slug, { status: newStatus });
            fetchEvents(currentPage);
        } catch (err) { alert('Gagal update status'); }
    };

    const confirmApproval = async () => {
        try {
            await updateEvent(approvingEvent.slug, { 
                status: 'approved',
                visibility: visibilityToggle
            });
            setShowApproveModal(false);
            setApprovingEvent(null);
            fetchEvents(currentPage);
        } catch (err) { alert('Gagal menyetujui event'); }
    };

    const handleCompleteSubmit = async (e) => {
        e.preventDefault();
        if (!completingEvent || completingLoading) return;
        setCompletingLoading(true);
        try {
            await completeEvent(completingEvent.slug, customBlastMessage, blastUseDefault);
            setShowCompleteModal(false);
            setCompletingEvent(null);
            fetchEvents(currentPage);
            alert('Event berhasil diselesaikan dan pesan WhatsApp blast telah dikirim!');
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.error || 'Gagal menyelesaikan event.');
        } finally {
            setCompletingLoading(false);
        }
    };

    const handleDelete = async (slug) => {
        if (!window.confirm('Yakin ingin menghapus event ini?')) return;
        try {
            await deleteEvent(slug);
            fetchEvents(currentPage);
        } catch (err) { alert('Gagal menghapus event'); }
    };

    return (
        <div className="body bg-gray-50 min-h-screen">
            <Helmet><title>Management Event - Admin Dashboard</title></Helmet>
            <Header />
            
            <div className="max-w-7xl mx-auto px-4 py-8 pb-24">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Management Event</h1>
                        <p className="text-sm text-gray-500">Tinjau dan kelola pengajuan event</p>
                    </div>
                    <Link 
                        to="/event/ajukan"
                        className="bg-gray-900 text-white px-6 py-3 rounded-2xl text-sm font-black flex items-center gap-2 shadow-xl hover:bg-gray-800 transition active:scale-95"
                    >
                        <span className="material-icons text-sm">add_circle</span>
                        BUAT EVENT BARU
                    </Link>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-wrap gap-4 mb-6">
                    <div className="flex-1 min-w-[200px] relative">
                        <span className="material-icons absolute left-3 top-2.5 text-gray-400 text-sm">search</span>
                        <input 
                            type="text" 
                            placeholder="Cari event..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-xl text-sm"
                        />
                    </div>
                    <select 
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2 bg-gray-50 border-none rounded-xl text-sm min-w-[150px]"
                    >
                        <option value="">Semua Status</option>
                        <option value="pending">Menunggu</option>
                        <option value="approved">Disetujui</option>
                        <option value="ongoing">Berjalan</option>
                        <option value="internal">Internal</option>
                        <option value="rejected">Ditolak</option>
                        <option value="completed">Selesai</option>
                    </select>
                </div>

                {/* Table Section */}
                <div className={`relative transition-all duration-500 ${loading ? 'opacity-70' : 'opacity-100'}`}>
                    {/* Loading Overlay for pagination/search changes */}
                    {loading && events.length > 0 && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/20 backdrop-blur-[1px] rounded-2xl">
                            <div className="bg-white/80 p-4 rounded-3xl shadow-xl flex items-center gap-3 border border-white">
                                <div className="animate-spin h-5 w-5 border-2 border-green-600 border-t-transparent rounded-full"></div>
                                <span className="text-xs font-black text-green-700 uppercase tracking-widest">Memperbarui...</span>
                            </div>
                        </div>
                    )}

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-4 py-4 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Event</th>
                                        <th className="px-4 py-4 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Penyelenggara</th>
                                        <th className="px-4 py-4 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Pembuat</th>
                                        <th className="px-4 py-4 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Biaya</th>
                                        <th className="px-4 py-4 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Waktu & Tempat</th>
                                        <th className="px-4 py-4 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Pendaftar</th>
                                        <th className="px-4 py-4 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Visibilitas</th>
                                        <th className="px-4 py-4 font-bold text-gray-600 uppercase tracking-wider text-[11px]">Status</th>
                                        <th className="px-4 py-4 font-bold text-gray-600 uppercase tracking-wider text-[11px] text-center">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 font-medium">
                                    {loading && events.length === 0 ? (
                                        <tr>
                                            <td colSpan="9" className="text-center py-20">
                                                <div className="flex flex-col items-center gap-4">
                                                    <div className="animate-spin h-8 w-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
                                                    <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Memuat data event...</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : events.length === 0 ? (
                                        <tr><td colSpan="9" className="text-center py-20 text-gray-400 italic">Tidak ada event ditemukan</td></tr>
                                    ) : events.map(ev => (
                                    <tr key={ev.id} className="hover:bg-gray-50/50 transition">
                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-3">
                                                <div 
                                                    className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 cursor-pointer"
                                                    onClick={() => setPreviewImage(getMediaUrl(ev.thumbnail_full || ev.thumbnail || '/placeholder-image.jpg'))}
                                                    title="Klik untuk lihat detail gambar asli"
                                                >
                                                    <img src={getMediaUrl(ev.thumbnail || '/placeholder-image.jpg')} alt="" className="w-full h-full object-cover" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-bold text-gray-900 line-clamp-1">{ev.title}</p>
                                                        {(ev.attachment_file || ev.attachment_link) && (
                                                            <span className="material-icons text-blue-500 text-xs" title="Memiliki Lampiran">attachment</span>
                                                        )}
                                                    </div>
                                                    <p className="text-[10px] text-gray-400">ID: {ev.id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-xs">
                                            <p className="text-gray-900 font-bold">{ev.organizer_name}</p>
                                            <p className="text-gray-400 mt-0.5">{ev.organizer_contact || '-'}</p>
                                        </td>
                                        <td className="px-4 py-4 text-[10px]">
                                            <p className="text-gray-600 font-bold">@{ev.created_by_details?.username || '-'}</p>
                                            <p className="text-gray-400">{ev.created_by_details?.email || '-'}</p>
                                        </td>
                                        <td className="px-4 py-4 text-xs font-black">
                                            <span className={`px-2 py-0.5 rounded text-[9px] ${ev.price_type === 'free' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>
                                                {ev.price_type === 'free' ? 'GRATIS' : `Rp ${Number(ev.price_fixed || 0).toLocaleString('id-ID')}`}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-xs whitespace-nowrap">
                                            <p className="text-gray-900">{new Date(ev.start_date).toLocaleDateString('id-ID')}</p>
                                            <p className="text-gray-500 mt-0.5 line-clamp-1 max-w-[120px]">{ev.location}</p>
                                        </td>
                                        <td className="px-4 py-4">
                                            <Link 
                                                to={`/dashboard/event/submissions/${ev.slug}`}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-black hover:bg-blue-600 hover:text-white transition shadow-sm border border-blue-100"
                                            >
                                                <span className="material-icons text-xs">people</span>
                                                {ev.registration_count || 0} DATA
                                            </Link>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider shadow-sm border ${
                                                ev.visibility === 'private' ? 'bg-purple-50 text-purple-700 border-purple-100' : 'bg-gray-50 text-gray-600 border-gray-100'
                                            }`}>
                                                {ev.visibility === 'private' ? 'RAHASIA' : 'UMUM'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                                ev.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                ev.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                                                ev.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                                ev.status === 'ongoing' ? 'bg-blue-100 text-blue-700' :
                                                ev.status === 'internal' ? 'bg-purple-100 text-purple-700' :
                                                'bg-gray-100 text-gray-600'
                                            }`}>
                                                {ev.status === 'pending' ? 'MENUNGGU' : 
                                                 ev.status === 'approved' ? 'DISETUJUI' : 
                                                 ev.status === 'rejected' ? 'DITOLAK' : 
                                                 ev.status === 'ongoing' ? 'BERJALAN' :
                                                 ev.status === 'internal' ? 'INTERNAL' :
                                                 ev.status === 'completed' ? 'SELESAI' : 
                                                 ev.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <button 
                                                    onClick={() => navigate(`/event/${ev.slug}`)}
                                                    className="w-8 h-8 rounded-lg bg-gray-50 text-gray-600 flex items-center justify-center hover:bg-gray-900 hover:text-white transition"
                                                    title="Lihat Event"
                                                >
                                                    <span className="material-icons text-sm">visibility</span>
                                                </button>
                                                
                                                {ev.status === 'pending' && (
                                                    <>
                                                        <button 
                                                            onClick={() => handleStatusUpdate(ev.slug, 'approved')}
                                                            className="w-8 h-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-600 hover:text-white transition"
                                                            title="Setujui"
                                                        >
                                                            <span className="material-icons text-sm">check</span>
                                                        </button>
                                                        <button 
                                                            onClick={() => handleStatusUpdate(ev.slug, 'rejected')}
                                                            className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition"
                                                            title="Tolak"
                                                        >
                                                            <span className="material-icons text-sm">close</span>
                                                        </button>
                                                    </>
                                                )}
                                                
                                                {['approved', 'ongoing', 'internal'].includes(ev.status) && (
                                                    <button 
                                                        onClick={() => {
                                                            setCompletingEvent(ev);
                                                            setBlastUseDefault(true);
                                                            setCustomBlastMessage('');
                                                            setShowCompleteModal(true);
                                                        }}
                                                        className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-600 hover:text-white transition"
                                                        title="Selesaikan Event"
                                                    >
                                                        <span className="material-icons text-sm">done_all</span>
                                                    </button>
                                                )}
                                                
                                                <button 
                                                    onClick={() => navigate(`/event/edit/${ev.slug}`)}
                                                    className="w-8 h-8 rounded-lg bg-gray-50 text-gray-600 flex items-center justify-center hover:bg-green-700 hover:text-white transition"
                                                    title="Edit Event"
                                                >
                                                    <span className="material-icons text-sm">edit</span>
                                                </button>

                                                <button 
                                                    onClick={() => handleDelete(ev.slug)}
                                                    className="w-8 h-8 rounded-lg bg-gray-50 text-gray-400 flex items-center justify-center hover:bg-red-600 hover:text-white transition"
                                                    title="Hapus"
                                                >
                                                    <span className="material-icons text-sm">delete</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                    {/* Pagination Placeholder */}
                    <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
                        <p>Showing {events.length} of {totalCount} events</p>
                        <div className="flex gap-2">
                            <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} className="px-3 py-1 bg-white border border-gray-200 rounded-md hover:bg-gray-50">Prev</button>
                            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} className="px-3 py-1 bg-white border border-gray-200 rounded-md hover:bg-gray-50">Next</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Preview Modal */}
            {previewImage && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-gray-900/90 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="relative w-full max-w-5xl h-[80vh] flex items-center justify-center">
                        <button
                            onClick={() => setPreviewImage(null)}
                            className="absolute -top-10 right-0 md:-right-10 w-10 h-10 bg-white/10 hover:bg-red-500 text-white rounded-full flex items-center justify-center transition backdrop-blur-sm z-[111]"
                        >
                            <span className="material-icons">close</span>
                        </button>
                        <img
                            src={getMediaUrl(previewImage)}
                            alt="Full Preview"
                            className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
                        />
                    </div>
                </div>
            )}

            {/* Approval Modal */}
            {showApproveModal && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="material-icons text-3xl">verified</span>
                            </div>
                            <h3 className="text-xl font-black text-gray-900 mb-2">Setujui Event</h3>
                            <p className="text-sm text-gray-500 mb-8">Apakah Anda yakin ingin menyetujui event <strong>{approvingEvent?.title}</strong>?</p>
                            
                            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 text-left mb-8">
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Pengaturan Visibilitas</p>
                                
                                <div className="space-y-3">
                                    <label className={`flex items-center gap-3 p-3 rounded-xl border-2 transition cursor-pointer ${visibilityToggle === 'public' ? 'bg-green-50 border-green-500' : 'bg-white border-gray-100'}`}>
                                        <input 
                                            type="radio" 
                                            name="visibility" 
                                            value="public" 
                                            checked={visibilityToggle === 'public'}
                                            onChange={() => setVisibilityToggle('public')}
                                            className="hidden"
                                        />
                                        <span className={`material-icons text-sm ${visibilityToggle === 'public' ? 'text-green-600' : 'text-gray-300'}`}>
                                            {visibilityToggle === 'public' ? 'radio_button_checked' : 'radio_button_unchecked'}
                                        </span>
                                        <div>
                                            <p className="text-xs font-bold text-gray-900">Publik (Kegiatan Kami)</p>
                                            <p className="text-[10px] text-gray-500 mt-0.5">Event akan tampil di beranda dan dapat ditemukan publik.</p>
                                        </div>
                                    </label>

                                    <label className={`flex items-center gap-3 p-3 rounded-xl border-2 transition cursor-pointer ${visibilityToggle === 'private' ? 'bg-purple-50 border-purple-500' : 'bg-white border-gray-100'}`}>
                                        <input 
                                            type="radio" 
                                            name="visibility" 
                                            value="private" 
                                            checked={visibilityToggle === 'private'}
                                            onChange={() => setVisibilityToggle('private')}
                                            className="hidden"
                                        />
                                        <span className={`material-icons text-sm ${visibilityToggle === 'private' ? 'text-purple-600' : 'text-gray-300'}`}>
                                            {visibilityToggle === 'private' ? 'radio_button_checked' : 'radio_button_unchecked'}
                                        </span>
                                        <div>
                                            <p className="text-xs font-bold text-gray-900">Internal (Hanya Link)</p>
                                            <p className="text-[10px] text-gray-500 mt-0.5">Event tersembunyi dari beranda, hanya untuk urusan internal.</p>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button 
                                    onClick={() => setShowApproveModal(false)}
                                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-600 rounded-2xl text-xs font-black hover:bg-gray-200 transition"
                                >
                                    BATAL
                                </button>
                                <button 
                                    onClick={confirmApproval}
                                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-2xl text-xs font-black hover:bg-green-700 transition shadow-lg shadow-green-100"
                                >
                                    SETUJUI EVENT
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Event Completion Modal */}
            {showCompleteModal && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-gray-950/65 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100 flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
                        {/* Header Banner */}
                        <div className="bg-gradient-to-r from-emerald-600 to-green-600 p-6 text-white text-center shrink-0">
                            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 shadow-inner">
                                <span className="material-icons text-2xl">verified</span>
                            </div>
                            <h2 className="text-lg sm:text-xl font-black uppercase tracking-wider">Selesaikan Event & Blast WA</h2>
                            <p className="text-white/80 text-xs mt-1">Selesaikan event dan kirimkan notifikasi otomatis ke semua peserta</p>
                        </div>

                        <form onSubmit={handleCompleteSubmit} className="p-6 flex flex-col overflow-hidden">
                            <div className="flex-1 overflow-y-auto space-y-5 pr-1" style={{ maxHeight: '50vh' }}>
                                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-xs text-emerald-800 leading-relaxed">
                                    <p className="font-bold mb-1">Pemberitahuan Sistem:</p>
                                    Tindakan ini akan mengubah status event menjadi <strong>SELESAI</strong> secara permanen. Sistem akan mengirimkan pesan WhatsApp blast 1x ke seluruh peserta yang pendaftarannya telah disetujui. Peserta juga dapat memberikan testimoni bintang 1-5 dan ulasan.
                                </div>

                                <div className="space-y-3">
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Pilih Template Pesan Blast</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <label className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition cursor-pointer ${blastUseDefault ? 'bg-emerald-50/50 border-emerald-500' : 'bg-white border-gray-200'}`}>
                                            <input 
                                                type="radio" 
                                                name="blast_type" 
                                                checked={blastUseDefault}
                                                onChange={() => setBlastUseDefault(true)}
                                                className="hidden"
                                            />
                                            <span className={`material-icons text-sm ${blastUseDefault ? 'text-emerald-600' : 'text-gray-300'}`}>
                                                {blastUseDefault ? 'radio_button_checked' : 'radio_button_unchecked'}
                                            </span>
                                            <span className="text-xs font-bold text-gray-700">Pesan Default</span>
                                        </label>

                                        <label className={`flex items-center gap-3 p-3 rounded-2xl border-2 transition cursor-pointer ${!blastUseDefault ? 'bg-emerald-50/50 border-emerald-500' : 'bg-white border-gray-200'}`}>
                                            <input 
                                                type="radio" 
                                                name="blast_type" 
                                                checked={!blastUseDefault}
                                                onChange={() => setBlastUseDefault(false)}
                                                className="hidden"
                                            />
                                            <span className={`material-icons text-sm ${!blastUseDefault ? 'text-emerald-600' : 'text-gray-300'}`}>
                                                {!blastUseDefault ? 'radio_button_checked' : 'radio_button_unchecked'}
                                            </span>
                                            <span className="text-xs font-bold text-gray-700">Tulis Pesan Sendiri</span>
                                        </label>
                                    </div>
                                </div>

                                {blastUseDefault ? (
                                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Pratinjau Pesan Default</p>
                                        <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono leading-relaxed bg-white border border-gray-100 rounded-xl p-3">
                                            {`Halo {name},\n\nTerima kasih telah berpartisipasi dalam event *{event}*.\n\nEvent ini telah selesai dilaksanakan. Kami sangat menghargai kehadiran dan partisipasi Anda.\n\nSilakan berikan testimoni Anda (bintang dan komentar) melalui link berikut:\n🔗 {event_link}\n\nTerima kasih!\nBarakah Economy`}
                                        </pre>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
                                            Isi Pesan WhatsApp Blast
                                        </label>
                                        <textarea
                                            required
                                            rows="6"
                                            placeholder="Tulis pesan blast Anda di sini..."
                                            value={customBlastMessage}
                                            onChange={(e) => setCustomBlastMessage(e.target.value)}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs outline-none focus:ring-2 focus:ring-emerald-500 transition font-mono"
                                        />
                                        
                                        {/* Guidelines */}
                                        <div className="bg-amber-50/50 border border-amber-200 rounded-2xl p-4">
                                            <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                                <span className="material-icons text-sm">info</span>
                                                Panduan Variabel (Placeholder):
                                            </p>
                                            <ul className="text-[10px] text-amber-700 space-y-1 font-medium list-disc pl-4">
                                                <li> gunakan <code className="bg-white px-1 py-0.5 rounded font-mono font-bold text-amber-800">{`{name}`}</code> untuk menyisipkan nama lengkap peserta.</li>
                                                <li> gunakan <code className="bg-white px-1 py-0.5 rounded font-mono font-bold text-amber-800">{`{event}`}</code> untuk menyisipkan nama/judul event ini.</li>
                                                <li> gunakan <code className="bg-white px-1 py-0.5 rounded font-mono font-bold text-amber-800">{`{event_link}`}</code> untuk menyisipkan link menuju detail event untuk mengisi testimoni.</li>
                                                <li> gunakan <code className="bg-white px-1 py-0.5 rounded font-mono font-bold text-amber-800">{`{time}`}</code> untuk menyisipkan waktu pelaksanaan event.</li>
                                                <li> gunakan <code className="bg-white px-1 py-0.5 rounded font-mono font-bold text-amber-800">{`{location}`}</code> untuk menyisipkan lokasi event.</li>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 mt-6 shrink-0 pt-4 border-t border-gray-100">
                                <button 
                                    type="button"
                                    disabled={completingLoading}
                                    onClick={() => setShowCompleteModal(false)}
                                    className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-2xl text-xs font-black transition disabled:opacity-50"
                                >
                                    BATAL
                                </button>
                                <button 
                                    type="submit"
                                    disabled={completingLoading || (!blastUseDefault && !customBlastMessage.trim())}
                                    className="flex-1 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-xs font-black transition shadow-lg shadow-emerald-100 disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {completingLoading ? (
                                        <span className="flex items-center gap-1">
                                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                            </svg>
                                            Memproses...
                                        </span>
                                    ) : (
                                        <>
                                            <span className="material-icons text-sm">done_all</span>
                                            SELESAIKAN EVENT
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <NavigationButton />
        </div>
    );
};

export default DashboardEventPage;
